import type { Category } from "../types";
import {
  nativeIframeSrc,
  nativeImageSrc,
  nativeLinkHref,
  nativeScriptSrc,
  nativeSetAttribute,
} from "../core/native";
import { isHandled, type BlockedKind } from "./blocked-items";
import { isExplicitlyAllowed, log, readTaggedCategory, type BlockingContext } from "./context";

/**
 * The strongest layer: intercepting the *setter* means we act before the browser has a
 * URL to fetch, so nothing leaves the device. A MutationObserver, by contrast, runs as a
 * microtask after insertion — by then the request is often already in flight.
 *
 * This covers every dynamically injected tag, which is how tag managers load their
 * downstream tools. It cannot cover tags hardcoded in the initial HTML — those must be
 * tagged in markup (see the auditor, which points them out).
 */
export function installPrototypePatches(context: BlockingContext): void {
  patchUrlProperty(HTMLScriptElement, "src", nativeScriptSrc, "script", context);
  patchUrlProperty(HTMLIFrameElement, "src", nativeIframeSrc, "iframe", context);
  patchUrlProperty(HTMLImageElement, "src", nativeImageSrc, "img", context);
  patchUrlProperty(HTMLLinkElement, "href", nativeLinkHref, "link", context);
  patchSetAttribute(context);
}

const WITHHELD = "__ckWithheldUrl";

type WithheldElement = Element & { [WITHHELD]?: string };

function patchUrlProperty(
  ctor: { prototype: Element },
  property: "src" | "href",
  descriptor: PropertyDescriptor | undefined,
  kind: BlockedKind,
  context: BlockingContext,
): void {
  if (!descriptor?.set || !descriptor.get) return;
  const nativeSet = descriptor.set;
  const nativeGet = descriptor.get;

  Object.defineProperty(ctor.prototype, property, {
    configurable: true,
    enumerable: descriptor.enumerable ?? true,
    get(this: WithheldElement): string {
      // Report the URL the caller asked for, so feature detection does not break.
      return this[WITHHELD] ?? String(nativeGet.call(this));
    },
    set(this: WithheldElement, value: string) {
      const category = decide(this, value, kind, context);
      if (category === null) {
        delete this[WITHHELD];
        nativeSet.call(this, value);
        return;
      }
      this[WITHHELD] = value;
      hold(this, value, category, kind, context);
    },
  });
}

/** `setAttribute("src", …)` bypasses the property setter, so it needs the same treatment. */
function patchSetAttribute(context: BlockingContext): void {
  if (!nativeSetAttribute) return;
  const native = nativeSetAttribute;

  Element.prototype.setAttribute = function patched(this: Element, name: string, value: string) {
    const lowered = name.toLowerCase();
    const kind = kindOf(this);
    const isUrlAttr = lowered === "src" || (lowered === "href" && kind === "link");

    if (kind && isUrlAttr) {
      const category = decide(this, value, kind, context);
      if (category !== null) {
        (this as WithheldElement)[WITHHELD] = value;
        hold(this, value, category, kind, context);
        return;
      }
    }
    return native.call(this, name, value);
  };
}

function kindOf(element: Element): BlockedKind | null {
  switch (element.tagName) {
    case "SCRIPT":
      return "script";
    case "IFRAME":
      return "iframe";
    case "IMG":
      return "img";
    case "LINK":
      return "link";
    default:
      return null;
  }
}

/** Returns the category standing in the way, or null if the URL may load now. */
function decide(
  element: Element,
  url: string,
  kind: BlockedKind,
  context: BlockingContext,
): Category | null {
  if (!url || isExplicitlyAllowed(element) || isHandled(element)) return null;

  const tagged = readTaggedCategory(element);
  if (tagged) return context.isGranted(tagged) ? null : tagged;

  // <link rel="stylesheet"> and friends are not trackers; only preconnect/prefetch to a
  // blocked host would be, and those carry no payload worth the breakage risk.
  if (kind === "link" && !isTrackingLink(element)) return null;

  const category = context.blocklist.categoryFor(url);
  if (!category) return null;
  return context.isGranted(category) ? null : category;
}

function isTrackingLink(element: Element): boolean {
  const rel = (element.getAttribute("rel") ?? "").toLowerCase();
  return rel.includes("preconnect") || rel.includes("dns-prefetch") || rel.includes("prefetch");
}

function hold(
  element: Element,
  url: string,
  category: Category,
  kind: BlockedKind,
  context: BlockingContext,
): void {
  if (kind === "script") {
    const script = element as HTMLScriptElement;
    const original = script.getAttribute("type") ?? "";
    context.items.add({ kind, source: "setter", element, url, category, scriptType: original });
    element.setAttribute("type", "text/plain");
  } else {
    context.items.add({ kind, source: "setter", element, url, category });
  }
  log(context, `blocked ${kind} (${category}):`, url);
}
